import {
  FeatherAlertTriangle,
  FeatherClipboardCheck,
  FeatherEye,
} from "@subframe/core";

import type { WorkspaceTab } from "@/lib/workspace/tabs";

interface WorkspaceBottomNavProps {
  activeTab: WorkspaceTab;
  showEvents: boolean;
  onTabChange: (tab: WorkspaceTab) => void;
}

/**
 * Thumb-friendly bottom navigation for the mobile Workspace (NAU-316). Mirrors
 * the desktop tab strip: To Do and Monitoring, plus Events for roles that can
 * report one (NAU-442).
 */
export function WorkspaceBottomNav({
  activeTab,
  showEvents,
  onTabChange,
}: WorkspaceBottomNavProps) {
  return (
    <nav
      aria-label="Workspace"
      className="fixed inset-x-0 bottom-0 z-30 hidden mobile:flex tablet:flex items-center gap-2 border-t border-solid border-neutral-border bg-white px-4 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]"
    >
      <BottomNavItem
        label="To Do"
        icon={<FeatherClipboardCheck className="text-heading-3" />}
        active={activeTab === "to-do"}
        onClick={() => onTabChange("to-do")}
      />
      <BottomNavItem
        label="Monitoring"
        icon={<FeatherEye className="text-heading-3" />}
        active={activeTab === "logs"}
        onClick={() => onTabChange("logs")}
      />
      {showEvents && (
        <BottomNavItem
          label="Events"
          icon={<FeatherAlertTriangle className="text-heading-3" />}
          active={activeTab === "events"}
          onClick={() => onTabChange("events")}
        />
      )}
    </nav>
  );
}

function BottomNavItem({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`flex grow shrink-0 basis-0 flex-col items-center gap-1 rounded-md px-4 py-2 text-caption-bold font-caption-bold transition-colors ${
        active
          ? "bg-primary-navy-50 text-primary-navy-700"
          : "text-subtext-color hover:text-default-font"
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
